import { useQueries } from "@tanstack/react-query";
import React, { useMemo } from "react";

import {
  ChartData,
  ChartCollectionData,
} from "../../../../models/ChartCollectionData";
import DataSvc from "../../../../services/dataSvc";
import { ChartButton } from "../../../Common/ChartButton";
import { useIndividualFacilityChart } from "../hooks/useIndividualFacilityChart";

import "./styles.scss";

export interface IFacilityDetailsAnalysisChartSelectProps {
  chartCollections: ChartCollectionData[];
  selectedChartIndex: number;
  onSelectChart: (chartId: number) => void;
  showDropdown?: boolean;
  showDescription?: boolean;
}

export const FacilityDetailsAnalysisChartSelect: React.FunctionComponent<
  IFacilityDetailsAnalysisChartSelectProps
> = (props) => {
  const {
    selectedChartIndex,
    onSelectChart,
    chartCollections,
    showDropdown = true,
    showDescription = true,
  } = props;

  const selectedChartId = chartCollections[selectedChartIndex]?.id;
  const { data: selectedChartData } =
    useIndividualFacilityChart(selectedChartId);

  const individualChartQueries = useQueries({
    queries: chartCollections.map((collection) => ({
      queryKey: ["chartData", collection.id],
      queryFn: async () => {
        const chartCollectionsById =
          await DataSvc.getChartCollectionsByCollectionId(
            collection.id.toString()
          );

        return (
          chartCollectionsById.find(
            ({ is_individual_use }: ChartData) => is_individual_use
          ) ?? null
        );
      },
      enabled: !!collection.id,
    })),
  });

  const sortedChartCollections = useMemo(() => {
    return chartCollections
      .map((collection, originalIndex) => {
        const chartData = individualChartQueries[originalIndex]?.data;

        return {
          collection,
          originalIndex,
          displayOrder: chartData?.display_order ?? Number.MAX_SAFE_INTEGER,
        };
      })
      .sort((a, b) => {
        if (a.displayOrder === b.displayOrder) {
          return a.originalIndex - b.originalIndex;
        }

        return a.displayOrder - b.displayOrder;
      });
  }, [chartCollections, individualChartQueries]);

  const handleClick = (index: number) => {
    onSelectChart(index);
  };

  return (
    <div className="chart-select-container">
      {showDropdown && (
        <div className="chart-select-row">
          {sortedChartCollections.map(({ collection, originalIndex }) => (
            <ChartButton
              key={collection.id}
              onClick={() => handleClick(originalIndex)}
              chartCollectionData={collection}
              isActive={selectedChartIndex === originalIndex}
            />
          ))}
        </div>
      )}
      {showDescription && selectedChartData?.description && (
        <div className="chart-description-row">
          <div className="chart-description">
            {selectedChartData.description}
          </div>
        </div>
      )}
    </div>
  );
};
